import { keepPreviousData, useMutation, useQueries, useQuery } from '@tanstack/react-query'

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import type { IAsset, IAssetCreate, IAssetTag, IAssetUpdate } from '~/types/assets-management'

import {
  getAssets,
  getAsset,
  updateAsset,
  batchAssetStatusUpdate,
  createAsset,
  getTagsKeys,
  batchAssetTagsUpdate,
  searchAssets,
} from '~/api/assets-management'

type SelectCallback<T> = (user: IAsset) => T

////////// KEYS //////////
export const assetsManagementKeys = {
  all: ['assets-management'] as const,
  details: (id?: string) => [...assetsManagementKeys.all, 'details', id] as const,
  list: (params?: AxiosRequestConfig['params']) => [...assetsManagementKeys.all, 'list', params] as const,
  search: (search: string) => [...assetsManagementKeys.all, 'search', search] as const,
  tagsKeys: () => [...assetsManagementKeys.all, 'tags-keys'] as const,
}

////////// QUERIES //////////
export const useAssetsListQuery = (
  params?: AxiosRequestConfig['params']
): UseQueryResult<{
  data: IAsset[]
  total: number
}> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => getAssets(params),
    queryKey: assetsManagementKeys.list(params),
  })
}

export const useAssetDetailsQuery = (id?: string): UseQueryResult<IAsset> => {
  return useQuery({
    enabled: !!id,
    queryFn: () => getAsset(id as string),
    queryKey: assetsManagementKeys.details(id),
  })
}

export const useAssetsTagsKeysQuery = (): UseQueryResult<string[]> => {
  return useQuery({ queryFn: getTagsKeys, queryKey: assetsManagementKeys.tagsKeys() })
}

export const useAssetsSearchQuery = (
  search: string
): UseQueryResult<{
  data: IAsset[]
  total: number
}> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => searchAssets(search),
    queryKey: assetsManagementKeys.search(search),
  })
}

////////// MULTI QUERIES //////////
export const useAssetDetailsQueries = <T = IAsset>(
  assetIds?: string[],
  select?: SelectCallback<T>
): UseQueryResult<T | IAsset>[] => {
  const queries =
    !assetIds || assetIds.length === 0
      ? [{ queryKey: assetsManagementKeys.details('no-data'), select }]
      : assetIds?.map((assetId) => {
          return {
            enabled: !!assetIds,
            queryFn: () => getAsset(assetId),
            queryKey: assetsManagementKeys.details(assetId),
            select,
          }
        })
  return useQueries({
    queries,
  })
}

////////// MUTATIONS //////////
export const useUpdateAssetMutation = (): UseMutationResult<void, unknown, IAssetUpdate> => {
  return useMutation({ mutationFn: updateAsset })
}

export const useCreateAssetMutation = (): UseMutationResult<void, unknown, IAssetCreate> => {
  return useMutation({ mutationFn: createAsset })
}

export const useBatchStatusUpdateMutation = (): UseMutationResult<
  void,
  unknown,
  { assets_status: { [key: string]: string } }
> => {
  return useMutation({ mutationFn: batchAssetStatusUpdate })
}

export const useBatchTagsUpdateMutation = (): UseMutationResult<
  void,
  unknown,
  { assets_tags: { [key: string]: Omit<IAssetTag, 'asset_id'>[] } }
> => {
  return useMutation({ mutationFn: batchAssetTagsUpdate })
}
